import { lock, unlock } from './scroll-lock.js';

const controllers = new WeakMap();
const FIELDS = 'title,product_type,vendor,variants.title,variants.sku';
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

// suggest.json prices are decimal strings ("29.99") in shop currency; format with the shop's money_format.
let moneyFormat = '${{amount}}';
function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const d = n.toFixed(2);
  const [whole, frac] = d.split('.');
  const group = (w, sep) => w.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
    if (k === 'amount') return group(whole, ',') + '.' + frac;
    if (k === 'amount_no_decimals') return group(whole, ',');
    if (k === 'amount_with_comma_separator') return group(whole, '.') + ',' + frac;
    if (k === 'amount_no_decimals_with_comma_separator') return group(whole, '.');
    return d;
  });
}

function row(p) {
  const li = el('li');
  const a = el('a');
  a.href = p.url;
  const box = el('span', 'sug__img');
  const src = p.featured_image && p.featured_image.url;
  if (src) {
    const img = document.createElement('img');
    img.src = src + (src.includes('?') ? '&' : '?') + 'width=128';
    img.alt = '';
    img.width = 64;
    img.height = 80;
    img.loading = 'lazy';
    img.decoding = 'async';
    box.append(img);
  }
  const price = el('span', 'sug__price');
  if (p.compare_at_price_max && Number(p.compare_at_price_max) > Number(p.price)) {
    price.append(el('s', null, money(p.compare_at_price_max)), ' ');
  }
  const varies = p.price_min && p.price_max && p.price_min !== p.price_max;
  price.append((varies ? 'From ' : '') + money(varies ? p.price_min : p.price));
  a.append(box, el('span', 'sug__name', p.title), price);
  li.append(a);
  return li;
}

function init(dialog) {
  if (dialog.dataset.searchInit) return;
  dialog.dataset.searchInit = '1';
  const ac = new AbortController();
  const { signal } = ac;
  controllers.set(dialog, ac);

  moneyFormat = dialog.dataset.moneyFormat || moneyFormat;
  const input = dialog.querySelector('input[type="search"]');
  const form = dialog.querySelector('[data-search-form]');
  const list = dialog.querySelector('[data-sug]');
  const live = dialog.querySelector('[data-live]');
  const toggle = () => document.querySelector('[data-search-toggle]');
  let opener = null;
  let locked = false;
  let timer = 0;
  let req = null;

  function clearResults() {
    list.textContent = '';
    list.hidden = true;
  }
  function announce(msg) {
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = msg; });
  }

  function open(from) {
    if (dialog.open) return;
    opener = from || (document.activeElement !== document.body ? document.activeElement : null) || toggle();
    dialog.showModal();
    if (!locked) { locked = true; lock(); }
    input.focus();
    input.select();
  }
  dialog.addEventListener('close', () => {
    if (locked) { locked = false; unlock(); }
    clearTimeout(timer);
    if (req) req.abort();
    const back = opener && opener.isConnected && !opener.disabled ? opener : toggle();
    opener = null;
    if (back) back.focus();
  }, { signal });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) { dialog.close(); return; }
    if (e.target.closest('[data-search-close]')) dialog.close();
  }, { signal });
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-search-toggle]');
    if (!t) return;
    e.preventDefault();
    open(t);
  }, { signal });

  dialog.addEventListener('keydown', (e) => {
    // A type=search input swallows the first Escape to clear itself; Escape should always close.
    if (e.key === 'Escape') { e.preventDefault(); dialog.close(); return; }
    if (e.key === 'Tab') {
      const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((n) =>
        !n.disabled && n.tabIndex >= 0 && !n.closest('[hidden]') && n.getClientRects().length > 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    // Typing from a focused suggestion goes back to the input and the key lands there.
    if (list.contains(document.activeElement) && !e.ctrlKey && !e.metaKey && !e.altKey
        && (e.key === 'Backspace' || e.key.length === 1)) {
      input.focus();
      return;
    }
    // Roving focus on the suggestion anchors.
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const rows = [...list.querySelectorAll('a')];
    if (!rows.length) return;
    const i = rows.indexOf(document.activeElement);
    e.preventDefault();
    if (e.key === 'ArrowDown') (rows[i + 1] || rows[rows.length - 1]).focus();
    else if (i <= 0) input.focus();
    else rows[i - 1].focus();
  }, { signal });

  async function suggest(q) {
    if (req) req.abort();
    req = new AbortController();
    const url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6'
      + '&resources[options][fields]=' + encodeURIComponent(FIELDS);
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: req.signal });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      if (input.value.trim() !== q) return;
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      list.textContent = '';
      products.forEach((p) => list.append(row(p)));
      list.hidden = products.length === 0;
      announce(products.length === 1 ? '1 suggestion' : products.length + ' suggestions');
    } catch (err) {
      if (err.name === 'AbortError') return;
      clearResults(); // plain form submit still works
    }
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) {
      if (req) req.abort();
      clearResults();
      return;
    }
    timer = setTimeout(() => suggest(q), 200);
  }, { signal });

  form.addEventListener('submit', (e) => {
    if (input.value.trim() === '') e.preventDefault();
  }, { signal });
}

const SEL = '[data-search-overlay]';
const boot = (scope) => scope.querySelectorAll(SEL).forEach(init);
boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll(SEL).forEach((r) => {
    const c = controllers.get(r);
    if (c) c.abort();
    delete r.dataset.searchInit;
  });
});
